/**
 * VendorPicker — FK combobox for selecting a vendor, with inline "+ Create".
 *
 * The canonical EntityPickerBase + QuickCreateDialog pattern, adapted for the
 * vendor's composite-create case: a vendor is an organization PLUS a procurement
 * vendor profile, so inline create opens VendorQuickCreateDialog (which creates
 * both) rather than a bare organization. See
 * docs/design-system/entity-picker-inline-create.md § Composite create.
 *
 * Value semantics: the selected value is the vendor's `organization_id` (this is
 * what a PO's `vendor_id` references). Items are vendor *profiles* remapped so
 * `id === organization_id`, which is what EntityPickerBase matches on.
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Building2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase, LtrToken, BidiText } from '../../../shared/components/common';
import { useSelectableVendors } from '../hooks/useVendors';
import { VendorQuickCreateDialog } from './VendorQuickCreateDialog';
import type { VendorProfile } from '../types';

interface VendorPickerItem {
  /** organization_id — the selectable value (PO.vendor_id references this) */
  id: string;
  /** procurement vendor-profile id — used for the detail-page link; null for a
   *  profile-less (import-created / intercompany) vendor org, which has no
   *  vendor detail page yet. */
  profileId: string | null;
  company_name: string;
  /** Vendor code, or '' for a profile-less vendor org. */
  vendor_code: string;
}

interface VendorPickerProps {
  /** Currently selected vendor organization_id ('' when none) */
  value: string;
  /** Callback when a vendor is selected (organization_id) or cleared ('') */
  onChange: (organizationId: string) => void;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder in the trigger when nothing is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show the "None" option to clear selection */
  allowClear?: boolean;
  /** Required indicator on the label */
  required?: boolean;
  /** Hide the "Add new" action (filter contexts) */
  hideAddNew?: boolean;
  /** Render the selected vendor as a link to its detail page */
  linkToEntity?: boolean;
}

export function VendorPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  required = false,
  hideAddNew = false,
  linkToEntity = false,
}: VendorPickerProps) {
  const { t } = useTranslation(['procurement', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: vendors } = useSelectableVendors();

  const items = useMemo<VendorPickerItem[]>(() => {
    const mapped = (vendors ?? []).map((v) => ({
      id: v.organization_id,
      profileId: v.vendor_profile_id,
      company_name: v.company_name,
      vendor_code: v.vendor_code ?? '',
    }));
    const query = search.trim().toLowerCase();
    if (!query) return mapped;
    return mapped.filter(
      (v) =>
        v.company_name.toLowerCase().includes(query) ||
        v.vendor_code.toLowerCase().includes(query),
    );
  }, [vendors, search]);

  const handleSelect = useCallback((organizationId: string) => {
    onChange(organizationId);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleAddNew = useCallback(() => {
    setOpen(false);
    setShowCreateDialog(true);
  }, []);

  const handleVendorCreated = useCallback((vendor: VendorProfile) => {
    onChange(vendor.organization_id);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange]);

  const selectedProfileId = useMemo(
    () => items.find((v) => v.id === value)?.profileId ?? null,
    [items, value],
  );

  return (
    <>
      <EntityPickerBase<VendorPickerItem>
        value={value}
        items={items}
        getItemLabel={(v) => v.company_name}
        renderItem={(v, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <Building2 className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <BidiText className="truncate">{v.company_name}</BidiText>
            {v.vendor_code && (
              <span className="ms-auto ps-2 text-xs text-muted-foreground shrink-0">
                <LtrToken>{v.vendor_code}</LtrToken>
              </span>
            )}
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('vendors.searchOrCreate')}
        searchPlaceholder={t('vendors.search')}
        emptyMessage={t('vendors.noVendorsFound')}
        disabled={disabled}
        className={className}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        hideAddNew={hideAddNew}
        addNewLabel={t('vendors.addNew')}
        onAddNew={handleAddNew}
        selectedEntityUrl={linkToEntity && selectedProfileId
          ? `/procurement/vendors/${selectedProfileId}`
          : undefined}
        entityIcon={linkToEntity ? Building2 : undefined}
      />

      <VendorQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleVendorCreated}
        skipNavigation
      />
    </>
  );
}
